import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './quizzes.css';
import Score from './Score';
import Questions from './Questions';
import SEO from '../../Components/SEO';
import { Button } from '@mui/material';
import quizzesData from './Data/QuizData.json';
import Breadcrumb from '../../Components/Breadcrumb';

const QUIZ_IMAGE = '/Images/quizzes1-1280x470.webp';
const QUESTION_COUNT = 10;
const SECONDS_PER_QUESTION = 10;

/* ---------- helpers ---------- */
const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const prepareQuestions = (questions) =>
  shuffleArray(questions)
    .slice(0, QUESTION_COUNT)
    .map((q) => ({ ...q, options: shuffleArray(q.options) }));

/* ---------- main component ---------- */

const QuizApp = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const quizSlug = searchParams.get('quiz');

  const [phase, setPhase] = useState('idle'); // 'idle' | 'playing' | 'finished'
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [timer, setTimer] = useState(SECONDS_PER_QUESTION);

  const activeQuiz = useMemo(
    () => quizzesData.find((q) => q.slug === quizSlug) || null,
    [quizSlug]
  );

  const isLastQuestion = currentQuestion === questions.length - 1;

  /* ---------- timer ---------- */
  useEffect(() => {
    if (phase !== 'playing') return;
    if (timer <= 0) {
      handleNextQuestion(true);
      return;
    }
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer, phase, currentQuestion]);

  /* ---------- actions ---------- */

  const startQuiz = useCallback((slug) => {
    const quiz = quizzesData.find((q) => q.slug === slug);
    if (!quiz) return;
    setQuestions(prepareQuestions(quiz.questions));
    setCurrentQuestion(0);
    setScore(0);
    setTimer(SECONDS_PER_QUESTION);
    setPhase('playing');
    setSearchParams({ quiz: slug });
  }, [setSearchParams]);

  const handleAnswerClick = useCallback((selectedAnswer) => {
    const correct = questions[currentQuestion]?.answer;
    if (selectedAnswer === correct) {
      setScore((s) => s + 1);
    }
  }, [questions, currentQuestion]);

  const handleNextQuestion = useCallback((timedOut = false) => {
    if (isLastQuestion) {
      setPhase('finished');
      return;
    }
    setCurrentQuestion((q) => q + 1);
    setTimer(SECONDS_PER_QUESTION);
  }, [isLastQuestion]);

  const restartQuiz = useCallback(() => {
    if (activeQuiz) startQuiz(activeQuiz.slug);
  }, [activeQuiz, startQuiz]);

  const resetToIdle = useCallback(() => {
    setPhase('idle');
    setQuestions([]);
    setCurrentQuestion(0);
    setScore(0);
    setTimer(SECONDS_PER_QUESTION);
    setSearchParams({});
  }, [setSearchParams]);

  const handleSelectQuiz = useCallback((e) => {
    const slug = e.target.value;
    if (!slug) {
      resetToIdle();
      return;
    }
    startQuiz(slug);
  }, [startQuiz, resetToIdle]);

  /* ---------- render ---------- */

  const renderCardBody = () => {
    // No quiz selected yet
    if (phase === 'idle') {
      return (
        <div className="quiz-idle-state">
          <p className="text-muted mb-0">
            Select a quiz from the dropdown above to begin.
          </p>
        </div>
      );
    }

    if (phase === 'playing') {
      return (
        <Questions
          questions={questions}
          currentQuestion={currentQuestion}
          timer={timer}
          isLastQuestion={isLastQuestion}
          handleAnswerClick={handleAnswerClick}
          handleNextQuestion={handleNextQuestion}
        />
      );
    }

    if (phase === 'finished') {
      return (
        <Score
          score={score}
          total={questions.length}
          questions={questions}
          onRestart={restartQuiz}
          onBack={resetToIdle}
        />
      );
    }

    return null;
  };

  return (
    <div>
      <SEO
        title={
          activeQuiz
            ? `Free English Quiz: ${activeQuiz.title.replace(/ in English$/, '')}`
            : 'Free English Vocabulary Quizzes'
        }
        description={
          activeQuiz?.description ||
          'A collection of vocabulary quizzes to strengthen your vocabulary.'
        }
        name="English Exam Exercises"
        type="article"
        image={QUIZ_IMAGE}
      />
      <Breadcrumb />

      <div className="home-container">
        <div className="homebodytext">
          <img src={QUIZ_IMAGE} alt="English Exam Exercises" />

          <div className="introtext">
            <h3 className="mt-2">
              {activeQuiz ? `${activeQuiz.title} Quiz` : 'Quizzes'}
            </h3>
          </div>

          <div className="page-description">
            {activeQuiz
              ? activeQuiz.description
              : 'Pick a quiz below. Each quiz draws 10 random questions from its pool. Keep checking back — new exercises are added regularly.'}
          </div>

          <div className="courses_container">
            <div className="quizzes_box">
              <div className="blog-card">
                <div className="description">
                  {/* ---------- SELECTOR ---------- */}
                  <div className="quiz-selector-row">
                    <label htmlFor="quiz-select" className="quiz-selector-label">
                      Choose a quiz:
                    </label>
                    <select
                      id="quiz-select"
                      className="form-select quiz-selector"
                      value={activeQuiz?.slug || ''}
                      onChange={handleSelectQuiz}
                    >
                      <option value="">— Select a quiz —</option>
                      {quizzesData.map((quiz) => (
                        <option key={quiz.slug} value={quiz.slug}>
                          {quiz.title}
                        </option>
                      ))}
                    </select>

                    {phase !== 'idle' && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={resetToIdle}
                      >
                        Change Quiz
                      </Button>
                    )}
                  </div>

                  {/* ---------- SINGLE CARD BODY ---------- */}
                  <div className="quiz-card-body">{renderCardBody()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizApp;